import { useEffect, useRef } from "react";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import dahlingLogo from "../../assets/dahling_logo_1.svg";
import { IconButton } from "../ui/IconButton";
import { cn } from "../../lib/utils";
import { acquireScrollLock, releaseScrollLock } from "../../lib/scroll-lock";
import { SidebarNav } from "./SidebarNav";

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus management: move focus to close button when open, restore on close
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      requestAnimationFrame(() => closeButtonRef.current?.focus());
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      acquireScrollLock();
      return () => releaseScrollLock();
    }
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm transition-opacity duration-300 lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-[260px] max-w-[85vw] flex-col overscroll-contain bg-white p-4 shadow-pop transition-transform duration-300 lg:hidden",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between px-3 pb-2">
          <img src={dahlingLogo} alt="Dahling" className="h-6 w-auto" />
          <IconButton
            ref={closeButtonRef}
            icon={CloseCircleLinear}
            aria-label="Close navigation"
            size="sm"
            onClick={onClose}
          />
        </div>
        <nav
          aria-label="Main navigation"
          className="no-scrollbar flex-1 overflow-y-auto pr-1 pt-1"
        >
          <SidebarNav onNavigate={onClose} />
        </nav>
      </aside>
    </>
  );
}
